import classNames from 'classnames';
import queryString from 'query-string';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearch } from 'wouter';

import useSourceFromQuery from '../../hooks/useSourceFromQuery';
import { useSubscription } from '../../hooks/useSubscription';

import { useSubscriptions } from '../../contexts/SiteContext';
import SubscriptionHorizontalCard from '../cards/SubscriptionHorizontalCard';
import GridList from '../lists/GridList';
import SectionDescription from '../typography/SectionDescription';
import SectionTitle from '../typography/SectionTitle';

import styles from '../../styles/panels/account-panel.module.css';

interface AccountSubscriptionsPanelProps {
    className?: string;
}

function AccountSubscriptionsPanel({ className }: AccountSubscriptionsPanelProps) {
    const allSubscriptions = useSubscriptions();
    const search = useSearch();
    const { unsubscribe: unsubscribeQuery = null, subscribe: subscribeQuery = null } =
        queryString.parse(search);
    const sourceQuery = useSourceFromQuery();

    const { subscribe, unsubscribe } = useSubscription({
        subscription: subscribeQuery || unsubscribeQuery,
        source: sourceQuery || 'link',
    });

    useEffect(() => {
        if (subscribeQuery !== null) {
            subscribe();
        } else if (unsubscribeQuery !== null) {
            unsubscribe();
        }
    }, [unsubscribeQuery, subscribeQuery]);

    const subscriptionQuery = subscribeQuery || unsubscribeQuery;
    const subscriptions = allSubscriptions
        .filter(({ type }) => type !== 'notification' && type !== 'external')
        .sort(({ handle: aHandle }, { handle: bHandle }) => {
            if (aHandle === subscriptionQuery) {
                return -1;
            }
            if (bHandle === subscriptionQuery) {
                return 1;
            }
            return 0;
        });
    const authorsNotifications = allSubscriptions.filter(
        ({ type, author = null }) => type === 'notification' && author !== null,
    );
    const externalSubscriptions = allSubscriptions.filter(({ type }) => type === 'external');

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <section className={styles.section}>
                <div className={styles.header}>
                    <SectionTitle className={styles.title}>
                        <FormattedMessage
                            defaultMessage="Infolettres"
                            description="Section title"
                        />
                    </SectionTitle>
                    <SectionDescription className={styles.description}>
                        <FormattedMessage
                            defaultMessage="Abonnez-vous par sms ou par courriel à nos infolettres"
                            description="Section description"
                        />
                    </SectionDescription>
                </div>
                <GridList<Subscription>
                    items={subscriptions}
                    cardComponent={SubscriptionHorizontalCard}
                    className={styles.list}
                    columns={1}
                    cardWith={['toggle']}
                    cardTheme="box"
                    cardRatio="square"
                    cardProps={{
                        source: sourceQuery || 'account',
                    }}
                />
            </section>

            {authorsNotifications.length > 0 ? (
                <section className={styles.section}>
                    <div className={styles.header}>
                        <SectionTitle className={styles.title}>Notifications</SectionTitle>
                        <SectionDescription className={styles.description}>
                            Recevez une notification par sms pour chaque contenu publié
                        </SectionDescription>
                    </div>

                    <GridList<Subscription>
                        items={authorsNotifications}
                        cardComponent={SubscriptionHorizontalCard}
                        className={styles.list}
                        cardWidth={300}
                        autoColumns
                        cardWith={['toggle']}
                        cardTheme="box"
                        cardRatio="square"
                        cardProps={{
                            source: sourceQuery || 'account',
                        }}
                    />
                </section>
            ) : null}

            {externalSubscriptions.length > 0 ? (
                <section className={styles.section}>
                    <div className={styles.header}>
                        <SectionTitle className={styles.title}>
                            <FormattedMessage
                                defaultMessage="Infolettres amies"
                                description="Section title"
                            />
                        </SectionTitle>
                        <SectionDescription className={styles.description}>
                            <FormattedMessage
                                defaultMessage="Ici on se fait un plaisir de vous recommander les infolettres de nos ami.e.s, du contenu intelligent, pertinent et divertissant. Abonnez-vous, vous ne serez pas déçus."
                                description="Section description"
                            />
                        </SectionDescription>
                    </div>
                    <GridList<Subscription>
                        items={externalSubscriptions}
                        cardComponent={SubscriptionHorizontalCard}
                        className={styles.list}
                        columns={1}
                        cardWith={['toggle']}
                        cardTheme="box"
                        cardRatio="square"
                        cardProps={{
                            source: sourceQuery || 'account',
                        }}
                    />
                </section>
            ) : null}
        </div>
    );
}

export default AccountSubscriptionsPanel;
